import type { MutationKey, QueryKey } from "@tanstack/react-query"

enum EStep {
    Profile = "profile",
    Video = "video",
    _LegacyModules = "module_list",
    Diagnostic = "diagnostic",
    _LegacyDiagnosticResponses = "diagnostic_responses",
    Reflection = "reflection_objective",
    Holds = "session_hold_times",
    Coach = "coach_select",
    Final = "finish"
}

const EQueryKeyBase: string = "OnboardingQuery" as const

const EQueryKey: {
    StepInfo(): QueryKey
    Steps: {
        Video(): QueryKey
        Diagnostic(): QueryKey
        Reflection(): QueryKey
        Holds(): QueryKey
        Coach: { Session(): QueryKey; Pairing(): QueryKey; Coaches(): QueryKey }
    }
} = {
    StepInfo: (): QueryKey => ["OnboardingSteps"],
    Steps: {
        Video: (): QueryKey => [EQueryKeyBase, EStep.Video],
        Diagnostic: (): QueryKey => [EQueryKeyBase, EStep.Diagnostic],
        Reflection: (): QueryKey => [EQueryKeyBase, EStep.Reflection],
        Holds: (): QueryKey => [EQueryKeyBase, EStep.Holds],
        Coach: {
            Session: (): QueryKey => [EQueryKeyBase, EStep.Coach, "Session"],
            Pairing: (): QueryKey => [EQueryKeyBase, EStep.Coach, "Pairing"],
            Coaches: (): QueryKey => [EQueryKeyBase, EStep.Coach, "Coaches"]
        }
    }
} as const

const EQueryUrl: {
    StepInfo(): string
    Steps: {
        Video(): string
        Diagnostic(): string
        Reflection(): string
        Holds(): string
        Coach: { Session(): string; Pairing(): string; Coaches(): string }
    }
} = {
    StepInfo: (): string => "/api/onboarding/step-information/",
    Steps: {
        Video: (): string => "/api/onboarding/welcome/",
        Diagnostic: (): string => "/api/onboarding/diagnostic/",
        Reflection: (): string => "/api/onboarding/reflection-objective/",
        Holds: (): string => "/api/session-hold/",
        Coach: {
            Session: (): string => "/api/onboarding/session-information/",
            Pairing: (): string => "/api/pairing-question/",
            Coaches: (): string => "/api/participants/available-individual-coaches/"
        }
    }
} as const

const EMutationKeyBase: string = "OnboardingMutation" as const

const EMutationKey: {
    Profile: { NextStep(): MutationKey }
    Video: { NextStep(): MutationKey }
    _LegacyModules: { NextStep(): MutationKey }
    Diagnostic: { NextStep(): MutationKey }
    _LegacyDiagnosticResponses: { NextStep(): MutationKey }
    Reflection: { NextStep(): MutationKey }
    Holds: { NextStep(): MutationKey }
    Coach: { NextStep(): MutationKey; Pairing(): MutationKey }
} = {
    Profile: { NextStep: (): MutationKey => [EMutationKeyBase, EStep.Profile] },
    Video: { NextStep: (): MutationKey => [EMutationKeyBase, EStep.Video] },
    _LegacyModules: { NextStep: (): MutationKey => [EMutationKeyBase, EStep._LegacyModules] },
    Diagnostic: { NextStep: (): MutationKey => [EMutationKeyBase, EStep.Diagnostic] },
    _LegacyDiagnosticResponses: {
        NextStep: (): MutationKey => [EMutationKeyBase, EStep._LegacyDiagnosticResponses]
    },
    Reflection: { NextStep: (): MutationKey => [EMutationKeyBase, EStep.Reflection] },
    Holds: { NextStep: (): MutationKey => [EMutationKeyBase, EStep.Holds] },
    Coach: {
        NextStep: (): MutationKey => [EMutationKeyBase, EStep.Coach],
        Pairing: (): MutationKey => [EMutationKeyBase, EStep.Coach, "Pairing"]
    }
} as const

const EMutationUrl: {
    Profile: { NextStep(): string }
    Video: { NextStep(): string }
    _LegacyModules: { NextStep(): string }
    Diagnostic: { NextStep(): string }
    _LegacyDiagnosticResponses: { NextStep(): string }
    Reflection: { NextStep(): string }
    Holds: { NextStep(): string }
    Coach: { NextStep(): string; Pairing(): string }
} = {
    Profile: { NextStep: (): string => "/api/onboarding/next/" },
    Video: { NextStep: (): string => "/api/onboarding/next/" },
    _LegacyModules: { NextStep: (): string => "/api/onboarding/next/" },
    Diagnostic: { NextStep: (): string => "/api/onboarding/next/" },
    _LegacyDiagnosticResponses: {
        NextStep: (): string => "/api/onboarding/next/"
    },
    Reflection: { NextStep: (): string => "/api/onboarding/next/" },
    Holds: { NextStep: (): string => "/api/onboarding/next/" },
    Coach: {
        NextStep: (): string => "/api/onboarding/next/",
        Pairing: (): string => "/api/participant-pairing-question-answer/"
    }
} as const

export {
    EStep as EParticipantOnboardingStep,
    EQueryKeyBase as EParticipantOnboardingQueryKeyBase,
    EQueryKey as EParticipantOnboardingQueryKey,
    EQueryUrl as EParticipantOnboardingQueryUrl,
    EMutationKeyBase as EParticipantOnboardingMutationKeyBase,
    EMutationKey as EParticipantOnboardingMutationKey,
    EMutationUrl as EParticipantOnboardingMutationUrl
}
